"use client";

import { useState } from "react";
import { lazyComponent } from "@/components/ui/lazy-component";
import { LoadFailureNotice } from "@/components/ui/load-failure-notice";
import { SlidersHorizontal } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { countActiveFilters } from "./listing-filters";
import type { FilterPanelProps } from "./listing-filter-panel";

const FilterPanel = lazyComponent(
  () => import("./listing-filter-panel").then((mod) => mod.FilterPanel),
  {
    loading: (
      <div className="px-4 py-4 text-center sm:px-6">
        <DialogTitle>Filters</DialogTitle>
        <DialogDescription>Loading filters…</DialogDescription>
      </div>
    ),
    failure: {
      label: "listing-filter-panel",
      fallback: (
        <div className="px-4 py-4 sm:px-6">
          <DialogTitle className="sr-only">Filters</DialogTitle>
          <LoadFailureNotice title="Filters could not load." />
        </div>
      ),
    },
  },
);

export function ListingFiltersDialog(props: FilterPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const activeCount = countActiveFilters(props.filters);
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" aria-label={activeCount > 0 ? `Filters, ${activeCount} applied` : "Filters"}>
          <SlidersHorizontal aria-hidden />
          Filters
          {activeCount > 0 ? (
            <Badge size="sm" tone="brand" solid numeric aria-hidden>
              {activeCount}
            </Badge>
          ) : null}
        </Button>
      </DialogTrigger>
      <DialogContent layout="sectioned" className="max-w-2xl">
        <FilterPanel
          {...props}
          onApply={(filters) => {
            props.onApply(filters);
            setIsOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
